import { useEffect, useState } from "react";
import api from "../../lib/api";
import { itDate } from "../../lib/constants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { today } from "./meta";

const F = ({ label, children, wide }) => <div className={wide ? "sm:col-span-2" : ""}><Label className="text-xs">{label}</Label>{children}</div>;

function useForm(sup) {
  const [form, setForm] = useState(sup);
  useEffect(() => { setForm(sup); /* eslint-disable-next-line */ }, [sup.id]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return [form, setForm, set];
}

const save = async (id, body, onSaved) => {
  try { const { data } = await api.put(`/suppliers/${id}`, body); toast.success("Dati salvati"); onSaved(data); }
  catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
};

const ANAG = [["name", "Nome / Ragione sociale *"], ["contact", "Referente"], ["phone", "Telefono"], ["email", "Email"], ["address", "Indirizzo"], ["vat", "P. IVA"], ["website", "Sito web / area riservata"]];

export function AnagraficaSection({ sup, onSaved }) {
  const [form, , set] = useForm(sup);
  return (
    <div className="card-soft p-4" data-testid="sec-anagrafica">
      <div className="grid sm:grid-cols-2 gap-3">
        {ANAG.map(([k, l]) => <F key={k} label={l}><Input data-testid={`anag-${k}`} value={form[k] || ""} onChange={set(k)} /></F>)}
        <F label="Note" wide><Textarea data-testid="anag-notes" rows={2} value={form.notes || ""} onChange={set("notes")} /></F>
      </div>
      <div className="flex justify-end mt-3"><Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="anag-save" onClick={() => save(sup.id, Object.fromEntries([...ANAG.map(([k]) => [k, form[k]]), ["notes", form.notes]]), onSaved)}>Salva anagrafica</Button></div>
    </div>
  );
}

export function CondizioniSection({ sup, onSaved }) {
  const [form, setForm, set] = useForm(sup);
  const promos = form.promotions || [];
  const setPromo = (i, k, v) => setForm((f) => ({ ...f, promotions: promos.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));
  const body = () => ({ payment_terms: form.payment_terms, min_order: form.min_order, below_min_fee: form.below_min_fee, below_min_notes: form.below_min_notes, facilitations: form.facilitations, conditions: form.conditions, promotions: promos });
  return (
    <div className="card-soft p-4" data-testid="sec-condizioni">
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Modalità e termini di pagamento" wide><Textarea data-testid="cond-payment_terms" rows={2} value={form.payment_terms || ""} onChange={set("payment_terms")} placeholder="es. Bonifico 60 gg data fattura fine mese" /></F>
        <F label="Ordine minimo (€)"><Input data-testid="cond-min_order" type="number" step="any" value={form.min_order ?? ""} onChange={set("min_order")} /></F>
        <F label="Spesa applicata sotto la soglia (€)"><Input data-testid="cond-below_min_fee" type="number" step="any" value={form.below_min_fee ?? ""} onChange={set("below_min_fee")} /></F>
        <F label="Note sulla soglia minima" wide><Input data-testid="cond-below_min_notes" value={form.below_min_notes || ""} onChange={set("below_min_notes")} /></F>
        <F label="Agevolazioni particolari" wide><Textarea data-testid="cond-facilitations" rows={2} value={form.facilitations || ""} onChange={set("facilitations")} /></F>
        <F label="Altre condizioni / note libere" wide><Textarea data-testid="cond-conditions" rows={3} value={form.conditions || ""} onChange={set("conditions")} /></F>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2"><p className="text-sm font-semibold">Promozioni con scadenza</p>
          <Button size="sm" variant="outline" data-testid="promo-add" onClick={() => setForm((f) => ({ ...f, promotions: [...promos, { title: "", expiry: "", notes: "" }] }))}><Plus size={14} className="mr-1" /> Promozione</Button></div>
        {promos.length === 0 && <p className="text-xs text-muted-foreground">Nessuna promozione.</p>}
        <div className="space-y-2">
          {promos.map((p, i) => (
            <div key={i} data-testid={`promo-${i}`} className={`grid grid-cols-[1fr_auto] sm:grid-cols-[2fr_1fr_2fr_auto] gap-2 p-2 rounded-lg ${p.expiry && p.expiry < today() ? "bg-slate-100 opacity-70" : "bg-amber-50"}`}>
              <Input data-testid={`promo-title-${i}`} placeholder="Promozione" value={p.title} onChange={(e) => setPromo(i, "title", e.target.value)} className="col-span-1" />
              <Input data-testid={`promo-expiry-${i}`} type="date" value={p.expiry} onChange={(e) => setPromo(i, "expiry", e.target.value)} className="order-3 sm:order-none" />
              <Input data-testid={`promo-notes-${i}`} placeholder="Note" value={p.notes} onChange={(e) => setPromo(i, "notes", e.target.value)} className="order-4 sm:order-none" />
              <Button size="icon" variant="outline" className="text-destructive" data-testid={`promo-del-${i}`} onClick={() => setForm((f) => ({ ...f, promotions: promos.filter((_, j) => j !== i) }))}><Trash2 size={14} /></Button>
              {p.expiry && <p className="text-[11px] text-muted-foreground col-span-full">{p.expiry < today() ? "Scaduta il" : "Valida fino al"} {itDate(p.expiry)}</p>}
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end mt-3"><Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="cond-save" onClick={() => save(sup.id, body(), onSaved)}>Salva condizioni</Button></div>
    </div>
  );
}
